import { assetUrl } from "@/i18n/assets";
import { Link } from "@/i18n/Link";
import { ArrowRight, Download, FileText, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { SITE_PHONE_DISPLAY } from "@/routes-data";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    seoTitle: "Custom Trailer Axle Order Forms - Elkhart, IN | Axle Inc.",
    seoDesc: "Download leaf-spring and torsion axle order forms from Axle Inc. in Elkhart, IN. Custom trailer axles built 1,000-10,000 lb by the area's Dexter distributor.",
    breadcrumbHome: "Home",
    breadcrumbForms: "Axle Order Forms",
    headerTag: "Custom Axle Orders",
    h1: "Axle order forms.",
    intro: "Axle Inc. builds custom trailer axles to your spec - sprung leaf-spring or rubber torsion, in capacities from 1,000 to 10,000 lb. Download the form that fits your application, fill in your dimensions, and send it back. We'll quote and confirm before anything goes on the build floor.",
    ctaCall: SITE_PHONE_DISPLAY,
    ctaMessage: "Send a message",
    formsTitle: "Downloadable order forms",
    formsDesc: "Print, fill in, and email completed forms to info@axleinc.com or fax to (574) 266-0042. If a measurement is unclear, leave it blank - we'll work through it with you on the call.",
    fileLabel: "PDF · 1 page",
    downloadForm: "Download form",
    forms: [
      { title: "Sprung (Leaf Spring) Axle Order Form", desc: "Specify tube diameter, capacity, drop configuration, brakes, hubs, hub-face & spring-center dimensions, spring pads, and replacement spring details." },
      { title: "Torsion Axle Order Form", desc: "Specify capacity, tube shape, brakes, hub face, bracket inside/outside, bracket direction, side-mount hangers, bolt pattern, stud size, and start angle." }
    ],
    axleTypesTitle: "Which axle do I need?",
    axleTypesDesc: "Two suspension styles cover almost every light-duty trailer on the road. If you're not sure which is right, call us - we'll walk through the application before you fill in a single dimension.",
    axleTypes: [
      { title: "Leaf Spring Axle", body: "Leaf spring axles are part of the spring suspension system, which helps the wheels on a trailer move freely from the truck body. They come in a number of variations depending on the trailer's weight and number of axles. Leaf spring suspension provides durability, even tire wear, and is a cost-effective option. Available in straight, drop, and drop-center configurations - as an idler axle or with electric, hydraulic, or hydraulic disc brakes. Capacities from 1,000 to 10,000 lb." },
      { title: "Rubber Torflex Axle", body: "Torflex axles, unlike their leaf-spring counterparts, are made up of rubber cords that provide suspension and shock absorption. They offer a smoother ride and longer-lasting components - best used for light- to medium-duty trailers. Available with electric, hydraulic, or hydraulic disc brakes." }
    ],
    orderFormBtn: "Order form",
    askQuestion: "Ask a question",
    requirementsTitle: "What we'll need from you",
    requirementsIntro: "Custom axles live or die on a few measurements. Before you start the form, have these ready:",
    reqs: [
      "Capacity - the rated load each axle has to carry, not the trailer's GVWR.",
      "Hub face dimension - outside of hub face to outside of hub face.",
      "Spring center (sprung axles) - center-to-center of spring pads.",
      "Bracket dimensions (torsion axles) - inside and outside of bracket.",
      "Bolt pattern and stud size - has to match your existing wheels.",
      "Brake type - electric, hydraulic drum, or hydraulic disc."
    ],
    plateNote: "If you can pull the spec plate off your existing axle and photograph it, that usually settles half the form by itself.",
    leadTimesTitle: "Lead times and pricing",
    leadTimesBody: "Standard configurations ship in days, not weeks - we keep the high-volume capacities and brake assemblies on the shelf. Unusual specs (long tubes, custom bracket geometry, mixed brake types) take a little longer; we'll quote both price and lead time on the same call.",
    ctaCallLong: (n: string) => `Call ${n}`,
    ctaDexter: "Authorized Dexter Distributor"
  },
  es: {
    seoTitle: "Formularios de Pedido de Ejes para Remolque - Elkhart, IN | Axle Inc.",
    seoDesc: "Descargue formularios de pedido de ejes de ballesta y torsión de Axle Inc. en Elkhart, IN. Ejes de remolque personalizados fabricados de 1,000 a 10,000 lb por el distribuidor de Dexter en el área.",
    breadcrumbHome: "Inicio",
    breadcrumbForms: "Formularios de Pedido de Ejes",
    headerTag: "Pedidos de Ejes Personalizados",
    h1: "Formularios de pedido de ejes.",
    intro: "Axle Inc. fabrica ejes de remolque personalizados según sus especificaciones: con suspensión de ballesta o torsión de caucho, en capacidades de 1,000 a 10,000 lb. Descargue el formulario que se ajuste a su aplicación, llene sus dimensiones y envíelo de vuelta. Cotizaremos y confirmaremos antes de que cualquier pedido pase a la planta de fabricación.",
    ctaCall: SITE_PHONE_DISPLAY,
    ctaMessage: "Enviar un mensaje",
    formsTitle: "Formularios de pedido descargables",
    formsDesc: "Imprima, llene y envíe los formularios completados por correo electrónico a info@axleinc.com o por fax al (574) 266-0042. Si una medida no está clara, déjela en blanco; lo resolveremos con usted durante la llamada.",
    fileLabel: "PDF · 1 página",
    downloadForm: "Descargar formulario",
    forms: [
      { title: "Formulario de Pedido de Eje (Ballesta)", desc: "Especifique el diámetro del tubo, capacidad, configuración de caída, frenos, mazas, dimensiones de cara de maza y centro de ballestas, soportes de ballestas y detalles de las ballestas de reemplazo." },
      { title: "Formulario de Pedido de Eje de Torsión", desc: "Especifique capacidad, forma del tubo, frenos, cara de la maza, interior/exterior del soporte, dirección del soporte, colgadores de montaje lateral, patrón de pernos, tamaño del espárrago y ángulo de inicio." }
    ],
    axleTypesTitle: "¿Qué eje necesito?",
    axleTypesDesc: "Dos estilos de suspensión cubren casi todos los remolques de carga ligera en la carretera. Si no está seguro de cuál es el adecuado, llámenos; revisaremos la aplicación antes de que llene una sola dimensión.",
    axleTypes: [
      { title: "Eje de Ballesta", body: "Los ejes de ballesta son parte del sistema de suspensión de muelles, lo que ayuda a que las ruedas de un remolque se muevan independientemente del cuerpo del camión. Vienen en varias variaciones según el peso del remolque y el número de ejes. La suspensión de ballesta proporciona durabilidad, desgaste uniforme de los neumáticos y es una opción rentable. Disponibles en configuraciones rectas, de caída y de centro caído, como eje loco o con frenos eléctricos, hidráulicos o de disco hidráulico. Capacidades de 1,000 a 10,000 lb." },
      { title: "Eje de Torsión de Caucho Torflex", body: "Los ejes Torflex, a diferencia de sus contrapartes de ballesta, están hechos de cordones de caucho que proporcionan suspensión y absorción de impactos. Ofrecen una conducción más suave y componentes de mayor duración; son ideales para remolques de carga ligera a media. Disponibles con frenos eléctricos, hidráulicos o de disco hidráulico." }
    ],
    orderFormBtn: "Formulario de pedido",
    askQuestion: "Hacer una pregunta",
    requirementsTitle: "Lo que necesitaremos de usted",
    requirementsIntro: "Los ejes personalizados dependen de algunas medidas. Antes de comenzar el formulario, tenga esto listo:",
    reqs: [
      "Capacidad: la carga nominal que cada eje debe soportar, no el GVWR del remolque.",
      "Dimensión de cara de maza: desde el exterior de la cara de la maza hasta el exterior de la cara de la maza.",
      "Centro de ballesta (ejes de ballesta): de centro a centro de los soportes de ballestas.",
      "Dimensiones del soporte (ejes de torsión): interior y exterior del soporte.",
      "Patrón de pernos y tamaño del espárrago: debe coincidir con sus ruedas existentes.",
      "Tipo de freno: eléctrico, tambor hidráulico o disco hidráulico."
    ],
    plateNote: "Si puede quitar la placa de especificaciones de su eje existente y fotografiarla, eso generalmente resuelve la mitad del formulario por sí solo.",
    leadTimesTitle: "Tiempos de entrega y precios",
    leadTimesBody: "Las configuraciones estándar se envían en días, no semanas; mantenemos las capacidades de alto volumen y los conjuntos de frenos en inventario. Las especificaciones inusuales (tubos largos, geometría de soporte personalizada, tipos de freno mixtos) toman un poco más de tiempo; cotizaremos tanto el precio como el tiempo de entrega en la misma llamada.",
    ctaCallLong: (n: string) => `Llamar al ${n}`,
    ctaDexter: "Distribuidor Autorizado de Dexter"
  }
};

export default function AxleOrderForms() {
  const t = useCopy(COPY);
  const trail = [
    { name: t.breadcrumbHome, path: "/" },
    { name: t.breadcrumbForms, path: "/axle-order-forms/" },
  ];
  return (
    <PageLayout>
      <Seo
        title={t.seoTitle}
        description={t.seoDesc}
        path="/axle-order-forms/"
        jsonLd={[organizationJsonLd(), breadcrumbJsonLd(trail)]}
      />
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4 inline-flex items-center gap-2">
            <FileText className="h-4 w-4" /> {t.headerTag}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
            {t.h1}
          </h1>
          <p className="mt-5 text-lg text-secondary-foreground/80 max-w-2xl leading-relaxed">
            {t.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {t.ctaCall}
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
              <Link to="/contact/">
                {t.ctaMessage} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 max-w-2xl">
          {t.formsTitle}
        </h2>
        <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
          {t.formsDesc}
        </p>
        <div className="grid gap-5 md:grid-cols-2">
          {[
            { ...t.forms[0], href: "/forms/sprung-axle-order-form.pdf" },
            { ...t.forms[1], href: "/forms/torsion-axle-order-form.pdf" }
          ].map((f) => (
            <a
              key={f.href}
              href={assetUrl(f.href)}
              target="_blank"
              rel="noopener"
              className="group bg-card border border-border rounded-md p-6 hover:border-primary/50 hover:shadow-md transition-all flex flex-col"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <FileText className="h-6 w-6 text-primary shrink-0" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t.fileLabel}
                </span>
              </div>
              <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                {f.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">{f.desc}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-strong">
                <Download className="h-4 w-4" /> {t.downloadForm}
              </span>
            </a>
          ))}
        </div>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-16 md:py-20">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 max-w-2xl">
            {t.axleTypesTitle}
          </h2>
          <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
            {t.axleTypesDesc}
          </p>
          <div className="grid gap-6 lg:grid-cols-2">
            {[
              { ...t.axleTypes[0], image: "/media/leaf-spring-axle.jpg", formHref: "/forms/sprung-axle-order-form.pdf" },
              { ...t.axleTypes[1], image: "/media/rubber-torflex-axle.jpg", formHref: "/forms/torsion-axle-order-form.pdf" }
            ].map((axle) => (
              <div key={axle.title} className="bg-card border border-border rounded-md overflow-hidden flex flex-col">
                <div className="aspect-[3/2] bg-muted overflow-hidden">
                  <img
                    src={assetUrl(axle.image)}
                    alt={`${axle.title} - Axle Inc.`}
                    width={1024}
                    height={683}
                    loading="lazy"
                    decoding="async"
                    className="block w-full h-full object-cover"
                  />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="text-xl font-semibold mb-3">{axle.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">{axle.body}</p>
                  <div className="flex flex-wrap gap-3">
                    <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                      <a href={assetUrl(axle.formHref)} target="_blank" rel="noopener">
                        <Download className="mr-1.5 h-4 w-4" /> {t.orderFormBtn}
                      </a>
                    </Button>
                    <Button asChild size="sm" variant="outline">
                      <Link to="/contact/">{t.askQuestion}</Link>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20 max-w-4xl">
        <div className="prose prose-neutral max-w-none">
          <h2>{t.requirementsTitle}</h2>
          <p>{t.requirementsIntro}</p>
          <ul>
            {t.reqs.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
          <p>{t.plateNote}</p>
          <h2>{t.leadTimesTitle}</h2>
          <p>{t.leadTimesBody}</p>
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
            <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
              <Phone className="mr-1.5 h-4 w-4" /> {t.ctaCallLong(SITE_PHONE_DISPLAY)}
            </a>
          </Button>
          <Button asChild variant="outline">
            <Link to="/contact/">{t.ctaMessage}</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/dexter/">{t.ctaDexter}</Link>
          </Button>
        </div>
      </section>
    </PageLayout>
  );
}
